import { get } from "@ember/object";
import dayjs from "dayjs";

import { arrayIfNeeded } from "parog-web/utils/xml-parsed-helpers";

export interface CodeAttributes {
  nullFlavor: unknown;
  codeSystem: string;
  code: string;
  displayName: string;
}

export interface Performer {
  assignedEntity: {
    id: {
      _attributes: CodeAttributes;
    };
  };
}

export interface Procedure {
  code: {
    _attributes: CodeAttributes;
  };
  performer: Performer | Array<Performer>;
}

export interface Entry {
  procedure?: Procedure;
  substanceAdministration?: object;
  supply?: object;
}

/**
 * A JSON section extracted from a structured reimbursement DMP file.
 */
export interface Section {
  code: {
    _attributes: {
      code: string;
    };
  };
  title: {
    _text: string;
  };
  entry: Entry;
}

export interface Execution {
  occurredAt: Date;
}

export default abstract class AbstractDmpParser {
  abstract journalEventClassName: string;

  abstract canParse(section: Section): boolean;

  getEntries(section: Section): Array<Entry> {
    return arrayIfNeeded(section.entry);
  }

  abstract parse(section: Section): Array<Execution>;

  /**
   * This function transforms a string to a Date Object
   * The expected string to be transformed must be "YYYYMMDD"
   */
  transformStringToDate(dateString: string | undefined): Date | undefined {
    if (!dateString) return;
    const date = dayjs(dateString, "YYYYMMDD").toDate();
    return new Date(date);
  }

  /**
   * Parses the date of procedure or encounter, using a default key path.
   * The key path can be overridden for specific use cases.
   */
  protected parseOccurredAt(
    object: unknown,
    keypath = "effectiveTime._attributes.value"
  ): Date | null {
    const effectiveTime = get(object, keypath) as string | null;
    const parsedOccurredAt = dayjs.utc(effectiveTime, "YYYYMMDD");
    if (!parsedOccurredAt.isValid) {
      console.error(
        `Invalid date when parsing effectiveTime: ${effectiveTime}`
      );
      return null;
    }
    return parsedOccurredAt.toDate();
  }
}
